// WebSocket 服务 - Vue3 TypeScript版本

// =============================
// 类型定义
// =============================

export interface WebSocketConfig {
  url: string
  deviceId?: string
  macAddress?: string
  deviceName?: string
  token?: string
  robotId?: string
  robotName?: string
  /** 是否启用多连接模式（文本和二进制分开） */
  multiConnection?: boolean
  /** 工作台先通过认证 REST 建立 canonical 会话，再由 WS 验证并附加连接。 */
  conversationSessionId?: string
  threadId?: string
  sourceType?: 'WORKBENCH'
}

export interface WebSocketMessage {
  type: 'hello' | 'stt' | 'tts' | 'llm' | 'listen' | 'audio' | 'video' | 'system' | 'text' | 'stream_meta' | 'device_registration' | 'session_init' | 'identity.recognized' | 'identity.switched' | 'identity.unknown'
  state?: 'start' | 'stop' | 'text' | 'sentence_start'
  text?: string
  /** 工作流 MESSAGE 节点的文本载荷，兼容旧 text/data 协议。 */
  content?: string
  data?: string
  session_id?: string
  mediaType?: 'audio' | 'video'  // 媒体类型标识
  codec?: string                  // 编解码器（opus、h264等）
  seq?: number                    // 流式消息序号
  size?: number                   // 数据大小
  timestamp?: number              // 时间戳
  [key: string]: any
}

export interface ChatMessage {
  id: string
  content: string
  type: 'text' | 'audio' | 'stt' | 'tts' | 'system'
  isUser: boolean
  timestamp: Date
  isLoading?: boolean
  duration?: string
  audioData?: any
}

export interface ConnectionStatus {
  isConnected: boolean
  connectionStatus: string
  connectionTime: Date | null
  sessionId: string | null
  conversationSessionId?: string | null
  connectionId?: string | null
  threadId?: string | null
  sourceType?: string | null
}

/** 当前已识别的身份信息 */
export interface CurrentIdentity {
  identityProfileId: number
  displayName: string
  recognitionType: string
  confidenceScore: number
  timestamp: Date
}

// =============================
// 状态变量
// =============================

// 主连接（兼容模式，同时处理文本和二进制）
let webSocket: WebSocket | null = null
// 文本专用连接
let textSocket: WebSocket | null = null
// 二进制专用连接
let binarySocket: WebSocket | null = null

let isConnecting = false
let reconnectTimer: number | null = null
let _reconnectAttempts = 0
const _maxReconnectAttempts = 5
const _reconnectDelay = 2000

// 是否启用多连接模式
let multiConnectionEnabled = false
let workbenchConnection = false

// 连接状态
const connectionStatus: ConnectionStatus = {
  isConnected: false,
  connectionStatus: '未连接',
  connectionTime: null,
  sessionId: null
}

// 消息列表
import { ref } from 'vue'
export const messages = ref<ChatMessage[]>([])

// 当前已识别的身份信息
export const currentRecognizedIdentity = ref<CurrentIdentity | null>(null)

// 回调函数
type MessageHandler = (data: WebSocketMessage) => void
type StatusChangeHandler = (status: ConnectionStatus) => void
type BinaryHandler = (data: ArrayBuffer) => void
type BinaryVideoHandler = (data: ArrayBuffer) => void

const messageHandlers: Set<MessageHandler> = new Set()
const statusChangeCallbacks: Set<StatusChangeHandler> = new Set()
let binaryHandler: BinaryHandler | null = null
let binaryVideoHandler: BinaryVideoHandler | null = null

// 元数据缓存：用于关联文本帧元数据和二进制数据帧
let lastStreamMeta: WebSocketMessage | null = null

type BinarySource = 'main' | 'binary'
let activeAudioBinarySource: BinarySource | null = null
let binarySendFrameCounter = 0
const REALTIME_DEBUG_HEAD_BYTES = 8

function formatBinaryHeadForDebug(data: ArrayBuffer | Uint8Array, maxBytes = REALTIME_DEBUG_HEAD_BYTES): string {
  const bytes = data instanceof Uint8Array ? data : new Uint8Array(data)
  return Array.from(bytes.slice(0, Math.min(maxBytes, bytes.length)))
    .map(byte => byte.toString(16).padStart(2, '0'))
    .join(' ')
}

function shouldLogRealtimeFrame(frameNo: number): boolean {
  return frameNo <= 5 || frameNo % 50 === 0
}

export const __testOnly = {
  formatBinaryHeadForDebug,
  shouldLogRealtimeFrame
}

// =============================
// 日志管理
// =============================

type LogLevel = 'debug' | 'info' | 'success' | 'warning' | 'error'

interface LogEntry {
  message: string
  type: LogLevel
  time: Date
}

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  success: 2,
  warning: 3,
  error: 4
}

let currentLogLevel = import.meta.env.DEV ? LOG_LEVELS.warning : LOG_LEVELS.info
let logHistory: LogEntry[] = []
const MAX_LOG_HISTORY = 500

export function log(message: string, type: LogLevel = 'info'): LogEntry {
  if (LOG_LEVELS[type] < currentLogLevel) {
    return { message, type, time: new Date() }
  }

  const entry: LogEntry = {
    message,
    type,
    time: new Date()
  }

  logHistory.push(entry)

  if (logHistory.length > MAX_LOG_HISTORY) {
    logHistory = logHistory.slice(-MAX_LOG_HISTORY)
  }

  switch (type) {
    case 'error':
      console.error(message)
      break
    case 'warning':
      console.warn(message)
      break
    case 'success':
      console.log('%c' + message, 'color: green')
      break
    case 'debug':
      console.debug(message)
      break
    default:
      console.log(message)
  }

  return entry
}

export function getLogs(): LogEntry[] {
  return [...logHistory]
}

export function clearLogs(): boolean {
  logHistory = []
  return true
}

export function setLogLevel(level: LogLevel): boolean {
  if (LOG_LEVELS[level] !== undefined) {
    currentLogLevel = LOG_LEVELS[level]
    return true
  }
  return false
}

// =============================
// 消息管理
// =============================

export function addMessage(message: Partial<ChatMessage>): ChatMessage | null {
  const content = message.content !== undefined && message.content !== null ? String(message.content) : ''
  const trimmedContent = content.trim()
  if (!trimmedContent && !message.isLoading) return null

  const newMessage: ChatMessage = {
    id: message.id || `msg_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
    content: trimmedContent,
    type: message.type || 'text',
    isUser: !!message.isUser,
    timestamp: message.timestamp || new Date(),
    isLoading: !!message.isLoading
  }

  messages.value.push(newMessage)

  log(
    `添加${newMessage.isUser ? '用户' : 'AI'}消息: ${newMessage.content.substring(0, 50)}${
      newMessage.content.length > 50 ? '...' : ''
    }`,
    'debug'
  )

  return newMessage
}

function updateMessage(messageId: string, updates: Partial<ChatMessage>): boolean {
  const idx = messages.value.findIndex(m => m.id === messageId)
  if (idx < 0) return false

  const current = messages.value[idx]
  if (!current) return false

  messages.value[idx] = {
    ...current,
    ...updates,
    id: updates.id ?? current.id,
    type: updates.type ?? current.type,
    isUser: updates.isUser ?? current.isUser,
    timestamp: updates.timestamp ?? current.timestamp,
    content: updates.content !== undefined ? String(updates.content) : current.content
  }
  return true
}

function removeMessage(messageId: string): boolean {
  const idx = messages.value.findIndex(m => m.id === messageId)
  if (idx < 0) return false
  messages.value.splice(idx, 1)
  return true
}

export function clearMessages(): boolean {
  messages.value.splice(0, messages.value.length)
  log('清空所有消息', 'info')
  return true
}

// =============================
// 回调管理
// =============================

export function registerMessageHandler(handler: MessageHandler): boolean {
  if (typeof handler === 'function') {
    messageHandlers.add(handler)
    return true
  }
  return false
}

export function unregisterMessageHandler(handler: MessageHandler): boolean {
  return messageHandlers.delete(handler)
}

export function registerStatusChangeCallback(callback: StatusChangeHandler): boolean {
  if (typeof callback === 'function') {
    statusChangeCallbacks.add(callback)
    return true
  }
  return false
}

export function unregisterStatusChangeCallback(callback: StatusChangeHandler): boolean {
  return statusChangeCallbacks.delete(callback)
}

export function registerBinaryHandler(handler: BinaryHandler): void {
  binaryHandler = handler
  console.log('✅ binaryHandler 已注册')
  log('binaryHandler 已注册', 'success')
}

export function registerBinaryVideoHandler(handler: BinaryVideoHandler): void {
  binaryVideoHandler = handler
}

// 通知状态变更
function notifyStatusChange(): void {
  const status = { ...connectionStatus }

  statusChangeCallbacks.forEach(callback => {
    try {
      callback(status)
    } catch (error) {
      log(`状态变更回调执行错误: ${error}`, 'error')
    }
  })
}

// =============================
// WebSocket 连接
// =============================

export async function connectToServer(config: WebSocketConfig): Promise<boolean> {
  if (webSocket && webSocket.readyState === WebSocket.OPEN) {
    log('WebSocket已连接', 'info')
    // 通知所有回调以同步状态（新注册的composable可能尚未收到状态）
    notifyStatusChange()
    return true
  }

  if (isConnecting) {
    log('WebSocket正在连接中...', 'info')
    return false
  }

  try {
    isConnecting = true
    connectionStatus.connectionStatus = '正在连接...'
    connectionStatus.isConnected = false
    connectionStatus.sessionId = null
    connectionStatus.conversationSessionId = null
    connectionStatus.connectionId = null
    connectionStatus.threadId = null
    connectionStatus.sourceType = null
    multiConnectionEnabled = config.multiConnection ?? false
    workbenchConnection = config.sourceType === 'WORKBENCH'

    // 清除之前的重连计时器
    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }

    // 关闭现有连接
    closeAllSockets()

    // 构建连接URL
    const baseUrl = config.url.endsWith('/') ? config.url.slice(0, -1) : config.url
    const params = buildConnectionParams(config)
    const queryString = params.toString()
    const mainUrl = baseUrl + '/' + (queryString ? '?' + queryString : '')

    log('正在连接 WebSocket 服务', 'info')

    // 创建主WebSocket连接
    const mainConnected = await createWebSocketConnection(mainUrl, 'main')
    if (!mainConnected) {
      return false
    }

    if (multiConnectionEnabled) {
      // 兼容 afsa 和  afsa 两种路径
      let textUrl: string
      let binaryUrl: string

      if (baseUrl.includes('/ws/afsa/v1')) {
        textUrl = baseUrl.replace('/ws/afsa/v1', '/ws/afsa/v1/text') + '/' + (queryString ? '?' + queryString : '')
        binaryUrl = baseUrl.replace('/ws/afsa/v1', '/ws/afsa/v1/binary') + '/' + (queryString ? '?' + queryString : '')
      } else {
        textUrl = baseUrl.replace('/ws/afsa/v1', '/ws/afsa/v1/text') + '/' + (queryString ? '?' + queryString : '')
        binaryUrl = baseUrl.replace('/ws/afsa/v1', '/ws/afsa/v1/binary') + '/' + (queryString ? '?' + queryString : '')
      }

      const [textOk, binaryOk] = await Promise.all([
        createWebSocketConnection(textUrl, 'text'),
        createWebSocketConnection(binaryUrl, 'binary')
      ])

      if (textOk) {
        log('文本专用连接已建立', 'success')
      } else {
        log('文本专用连接建立失败，将回退到主连接承载文本消息', 'warning')
      }

      if (binaryOk) {
        log('二进制专用连接已建立', 'success')
      } else {
        log('二进制专用连接建立失败，将回退到主连接承载音频/视频二进制数据', 'warning')
      }
    }

    return true
  } catch (error) {
    isConnecting = false
    connectionStatus.isConnected = false
    connectionStatus.connectionStatus = '连接失败'
    log(`WebSocket连接失败: ${error}`, 'error')
    notifyStatusChange()
    return false
  }
}

// 构建连接参数
function buildConnectionParams(config: WebSocketConfig): URLSearchParams {
  const params = new URLSearchParams()
  if (config.deviceId) {
    params.append('device-id', config.deviceId)
  }
  if (config.robotId) {
    params.append('robotId', config.robotId)
  }
  if (config.macAddress || config.deviceName) {
    params.append('mac_address', config.deviceName || config.macAddress || '')
  }
  if (config.token) {
    params.append('token', config.token)
  }
  if (config.sourceType === 'WORKBENCH') {
    params.append('sourceType', config.sourceType)
    if (config.conversationSessionId) params.append('conversationSessionId', config.conversationSessionId)
    if (config.threadId) params.append('threadId', config.threadId)
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
      if (typeof timezone === 'string' && timezone.length > 0 && timezone.length <= 64) {
        // 只上报浏览器真实且有效的时区；后端仍须在认证和连接绑定后独立校验。
        new Intl.DateTimeFormat('en', { timeZone: timezone })
        params.append('clientTimezone', timezone)
      }
    } catch {
      // 无法读取时保留缺失状态，不能用预设地区冒充当地时间。
    }
  }
  return params
}

// 关闭所有Socket连接
function closeAllSockets(reason?: 'USER_CLOSE' | 'LOGOUT'): void {
  [webSocket, textSocket, binarySocket].forEach(socket => {
    if (socket) {
      try {
        socket.close(1000, reason)
      } catch {
        // 忽略关闭错误
      }
    }
  })
  webSocket = null
  textSocket = null
  binarySocket = null
  activeAudioBinarySource = null
}

// 创建WebSocket连接
async function createWebSocketConnection(url: string, type: 'main' | 'text' | 'binary'): Promise<boolean> {
  return new Promise((resolve) => {
    const socket = new WebSocket(url)
    socket.binaryType = 'arraybuffer'

    const timeoutId = setTimeout(() => {
      log(`${type}连接超时`, 'warning')
      socket.close()
      resolve(false)
    }, 5000)

    socket.onopen = () => {
      clearTimeout(timeoutId)

      // 根据类型存储到对应变量
      switch (type) {
        case 'main':
          webSocket = socket
          isConnecting = false
          connectionStatus.isConnected = true
          connectionStatus.connectionStatus = '已连接'
          connectionStatus.connectionTime = new Date()
          _reconnectAttempts = 0
          notifyStatusChange()
          break
        case 'text':
          textSocket = socket
          break
        case 'binary':
          binarySocket = socket
          activeAudioBinarySource = 'binary'
          break
      }

      log(`WebSocket${type === 'main' ? '主' : type === 'text' ? '文本' : '二进制'}连接已建立`, 'success')
      resolve(true)
    }

    socket.onmessage = (event) => {
      // 二进制连接只处理二进制数据
      if (type === 'binary') {
        if (typeof event.data === 'string') {
          handleWebSocketMessage(event)
          return
        }

        if (event.data instanceof ArrayBuffer) {
          if (lastStreamMeta && lastStreamMeta.mediaType === 'video') {
            if (binaryVideoHandler) {
              binaryVideoHandler(event.data)
              log(
                `二进制连接收到视频数据: ${event.data.byteLength} bytes, codec: ${lastStreamMeta.codec}`,
                'debug'
              )
            } else {
              log('二进制连接收到视频数据，但 binaryVideoHandler 未注册！', 'warning')
            }
            lastStreamMeta = null
            return
          }

          if (!activeAudioBinarySource) {
            activeAudioBinarySource = 'binary'
            log('已选择二进制连接作为音频源', 'debug')
          }

          if (activeAudioBinarySource !== 'binary') {
            log('已选择主连接作为音频源，忽略二进制连接音频帧', 'debug')
            return
          }

          if (binaryHandler) {
            console.log(`🔊 二进制连接收到数据: ${event.data.byteLength} bytes，调用 binaryHandler`)
            log(`二进制连接收到数据: ${event.data.byteLength} bytes，调用 binaryHandler`, 'debug')
            binaryHandler(event.data)
          } else {
            console.log('⚠️ 二进制连接收到数据，但 binaryHandler 未注册！')
            log(`二进制连接收到数据: ${event.data.byteLength} bytes，但 binaryHandler 未注册！`, 'warning')
          }
        }
        return
      }
      // 文本连接和主连接处理所有消息
      handleWebSocketMessage(event)
    }

    socket.onclose = (event) => {
      clearTimeout(timeoutId)

      if (type === 'main') {
        // 仅当关闭的是当前主连接时才更新状态，防止旧socket的onclose覆盖新连接状态
        if (socket !== webSocket && webSocket !== null) {
          log(`忽略旧主连接的关闭事件 (当前已有新连接)`, 'debug')
          return
        }
        isConnecting = false
        connectionStatus.isConnected = false
        connectionStatus.sessionId = null
        connectionStatus.conversationSessionId = null
        connectionStatus.connectionId = null
        connectionStatus.threadId = null
        connectionStatus.sourceType = null
        if (event.wasClean) {
          connectionStatus.connectionStatus = '已断开'
          log(`WebSocket连接已关闭: 代码=${event.code}`, 'info')
        } else {
          connectionStatus.connectionStatus = '连接已断开'
          log('WebSocket连接意外断开', 'warning')
        }
        notifyStatusChange()
      } else {
        log(`${type}连接已关闭`, 'info')
        if (type === 'text') textSocket = null
        if (type === 'binary') {
          binarySocket = null
          activeAudioBinarySource =
            webSocket && webSocket.readyState === WebSocket.OPEN ? 'main' : null
        }
      }
    }

    socket.onerror = () => {
      clearTimeout(timeoutId)
      if (type === 'main') {
        isConnecting = false
        connectionStatus.isConnected = false
        connectionStatus.connectionStatus = '连接错误'
        notifyStatusChange()
      }
      log(`${type}连接错误`, 'warning')
      resolve(false)
    }
  })
}

// 处理WebSocket消息
function handleWebSocketMessage(event: MessageEvent): void {
  try {
    // 检查是否是二进制数据
    if (event.data instanceof ArrayBuffer) {
      // 根据最近的元数据判断类型
      if (lastStreamMeta && lastStreamMeta.mediaType === 'video') {
        // 视频数据
        if (binaryVideoHandler) {
          binaryVideoHandler(event.data)
          log(`分发视频二进制数据: ${event.data.byteLength} bytes, codec: ${lastStreamMeta.codec}`, 'debug')
        } else {
          log('未注册视频二进制消息处理函数', 'warning')
        }
        lastStreamMeta = null
      } else {
        // 默认为音频数据（向后兼容）
        if (!activeAudioBinarySource) {
          activeAudioBinarySource = 'main'
          log('已选择主连接作为音频源', 'debug')
        }

        if (activeAudioBinarySource !== 'main') {
          log('已选择二进制连接作为音频源，忽略主/文本连接音频帧', 'debug')
          return
        }

        if (binaryHandler) {
          console.log(`🔊 主连接收到二进制数据: ${event.data.byteLength} bytes`)
          binaryHandler(event.data)
          log(`分发音频二进制数据: ${event.data.byteLength} bytes`, 'info')
        } else {
          console.log('⚠️ 主连接收到二进制数据，但 binaryHandler 未注册！')
          log('未注册二进制消息处理函数', 'warning')
        }
      }
      return
    }

    // 处理文本数据
    const data: WebSocketMessage = JSON.parse(event.data)

    // ⭐ 立即执行的测试日志，证明代码已更新
    console.log('='.repeat(80))
    console.log('✅✅✅ 代码已更新为最新版本 (2026-01-01 20:20) ✅✅✅')
    console.log('='.repeat(80))

    // 调试：打印接收到的所有消息
    console.log('📨 收到WebSocket消息:', data)
    console.log('📋 消息原始JSON:', event.data)
    console.log('🔑 消息type字段:', data.type, '类型:', typeof data.type)

    // 记录会话ID（兼容 snake_case 和 camelCase），始终更新以防重连后sessionId变化
    const receivedSessionId = data.session_id || data.sessionId
    if (receivedSessionId && receivedSessionId !== connectionStatus.sessionId) {
      connectionStatus.sessionId = receivedSessionId
      log(`会话ID: ${connectionStatus.sessionId}`, 'info')
      notifyStatusChange()
    }

    // 处理流式元数据
    if (data.type === 'stream_meta') {
      lastStreamMeta = data
      log(`收到流式元数据: mediaType=${data.mediaType}, codec=${data.codec}, seq=${data.seq}`, 'debug')
      return
    }

    // 调试：switch之前再次检查type
    console.log('🔍 进入switch之前, data.type =', data.type, '是否等于"device_registration":', data.type === 'device_registration')

    // 根据消息类型处理
    switch (data.type) {
      case 'stt':
        handleSTTMessage(data)
        break
      case 'tts':
        handleTTSMessage(data)
        break
      case 'llm':
        handleLLMMessage(data)
        break
      case 'text':
        handleTextMessage(data)
        break
      case 'device_registration':
        console.log('✅ 匹配到 device_registration case')
        handleDeviceRegistrationMessage(data)
        break
      case 'identity.recognized':
        handleIdentityRecognized(data)
        break
      case 'identity.switched':
        handleIdentitySwitched(data)
        break
      case 'identity.unknown':
        handleIdentityUnknown(data)
        break
      case 'session_init':
        // 统一会话身份仅采纳服务端 attach 完成通知；物理 sessionId 保留兼容路由。
        connectionStatus.conversationSessionId = data.conversationSessionId || null
        connectionStatus.connectionId = data.connectionId || null
        connectionStatus.threadId = data.threadId || null
        connectionStatus.sourceType = data.sourceType || null
        notifyStatusChange()
        log('收到会话初始化通知', 'info')
        break
      case 'system':
        if (data.message || data.text) {
          log(`绯荤粺娑堟伅: ${data.message || data.text}`, 'info')
        }
        break
      default:
        // 检查是否是握手响应 (HelloMessageResp)
        if ((data as any).transport && (data as any).sessionId) {
           log(`收到握手响应: transport=${(data as any).transport}`, 'info')
        } else {
           log(`收到未知类型的消息: ${data.type}`, 'warning')
        }
    }

    // 调用所有注册的消息处理函数
    messageHandlers.forEach(handler => {
      try {
        handler(data)
      } catch (error) {
        log(`消息处理函数执行错误: ${error}`, 'error')
      }
    })
  } catch (error) {
    log(`处理WebSocket消息出错: ${error}`, 'error')
  }
}

// 处理STT消息（语音识别）
function handleSTTMessage(data: WebSocketMessage): void {
  console.log('🎤 handleSTTMessage 被调用，data:', data)
  console.log('🎤 data.text:', data.text, '类型:', typeof data.text)

  if (data.text) {
    console.log('🎤 准备添加 STT 消息到列表:', data.text)
    const result = addMessage({
      content: data.text,
      type: 'stt',
      isUser: true
    })
    console.log('🎤 addMessage 返回结果:', result)
    console.log('🎤 当前 messages 列表长度:', messages.value.length)
    log(`语音识别结果: ${data.text}`, 'info')
  } else {
    console.log('🎤 data.text 为空，不添加消息')
  }
}

let pendingAiTextBuffer = ''
let pendingAiTextMessageId: string | null = null
let pendingAiTextFromTextStream = false

function ensurePendingAiTextMessage(): string {
  if (pendingAiTextMessageId) return pendingAiTextMessageId

  const msg = addMessage({
    content: '',
    type: 'text',
    isUser: false,
    isLoading: true
  })

  pendingAiTextMessageId = msg?.id ?? `msg_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`

  if (!msg) {
    messages.value.push({
      id: pendingAiTextMessageId,
      content: '',
      type: 'text',
      isUser: false,
      timestamp: new Date(),
      isLoading: true
    })
  }

  return pendingAiTextMessageId
}

function flushPendingAiText(): void {
  if (!pendingAiTextMessageId) return

  const content = pendingAiTextBuffer.trim()
  if (!content) {
    removeMessage(pendingAiTextMessageId)
  } else {
    updateMessage(pendingAiTextMessageId, {
      content,
      isLoading: false
    })
  }

  pendingAiTextBuffer = ''
  pendingAiTextMessageId = null
  pendingAiTextFromTextStream = false
}

// 处理 LLM 流式文本消息
function handleLLMMessage(data: WebSocketMessage): void {
  const textContent = data.text || ''

  if (data.end === true) {
    flushPendingAiText()
    return
  }

  if (!textContent) return

  ensurePendingAiTextMessage()
  pendingAiTextBuffer += textContent
  updateMessage(pendingAiTextMessageId!, {
    content: pendingAiTextBuffer.trim(),
    isLoading: false
  })
  log(`收到LLM消息: ${textContent}`, 'info')
}

export function __handleIncomingWebSocketMessageForTest(data: WebSocketMessage): void {
  handleWebSocketMessage(new MessageEvent('message', { data: JSON.stringify(data) }))
}

// 处理设备注册消息
function handleDeviceRegistrationMessage(data: WebSocketMessage): void {
  console.log('🔔 处理设备注册消息:', data)

  const registrationCode = (data as any).registrationCode
  const deviceId = (data as any).deviceId
  const message = (data as any).message

  console.log('注册码:', registrationCode, '设备ID:', deviceId, '消息:', message)

  if (registrationCode) {
    log(`收到设备注册码: ${registrationCode}, 设备ID: ${deviceId}`, 'success')

    // 显示注册码信息
    addMessage({
      content: message || `新设备已连接，注册码: ${registrationCode}`,
      type: 'system',
      isUser: false
    })

    // 调用所有注册的消息处理函数，让它们可以处理这个注册码消息
    messageHandlers.forEach(handler => {
      try {
        handler({
          ...data,
          registrationCode,
          deviceId,
          message
        })
      } catch (error) {
        log(`设备注册消息处理错误: ${error}`, 'error')
      }
    })
  }
}

// 处理身份识别事件：identity.recognized
function handleIdentityRecognized(data: WebSocketMessage): void {
  const identityProfileId = (data as any).identityProfileId as number
  const displayName = (data as any).displayName as string || '未知'
  const recognitionType = (data as any).recognitionType as string || 'UNKNOWN'
  const confidenceScore = (data as any).confidenceScore as number || 0

  currentRecognizedIdentity.value = {
    identityProfileId,
    displayName,
    recognitionType,
    confidenceScore,
    timestamp: new Date()
  }

  addMessage({
    content: `已识别身份：${displayName}（${recognitionType}，置信度 ${Math.round(confidenceScore * 100)}%）`,
    type: 'system',
    isUser: false
  })

  log(`身份识别成功: ${displayName}, type=${recognitionType}, score=${confidenceScore}`, 'info')
}

// 处理身份识别事件：identity.switched
function handleIdentitySwitched(data: WebSocketMessage): void {
  const fromIdentity = (data as any).fromDisplayName as string || '未知'
  const toIdentity = (data as any).toDisplayName as string || '未知'
  const reason = (data as any).reason as string || ''

  log(`身份配置切换: ${fromIdentity} -> ${toIdentity}${reason ? ', 原因: ' + reason : ''}`, 'info')
}

// 处理身份识别事件：identity.unknown
function handleIdentityUnknown(data: WebSocketMessage): void {
  currentRecognizedIdentity.value = null

  const reason = (data as any).reason as string || '无法识别当前用户'
  addMessage({
    content: `身份未识别：${reason}`,
    type: 'system',
    isUser: false
  })

  log(`身份未识别: ${reason}`, 'warning')
}

// 处理TTS消息（文本转语音）
function handleTTSMessage(data: WebSocketMessage): void {
  if (data.state === 'start') {
    log('TTS开始', 'info')
  } else if (data.state === 'sentence_start' && data.text) {
    if (!pendingAiTextFromTextStream) {
      ensurePendingAiTextMessage()
      pendingAiTextBuffer += data.text
    }
    log(`TTS文本: ${data.text}`, 'info')
  } else if (data.state === 'stop') {
    log('TTS结束', 'info')
    flushPendingAiText()
  }
}

// 处理文本消息（流式文本响应）
function handleTextMessage(data: WebSocketMessage): void {
  // 解码 data 字段（Base64编码的文本）
  let textContent = ''
  if (data.data) {
    try {
      textContent = decodeURIComponent(escape(atob(data.data)))
    } catch {
      textContent = data.data
    }
  } else if (data.text) {
    textContent = data.text
  } else if (typeof data.content === 'string') {
    textContent = data.content
  }

  const isStreamingText = data.seq !== undefined || data.last === true
  if (isStreamingText) {
    pendingAiTextFromTextStream = true
    ensurePendingAiTextMessage()
    pendingAiTextBuffer += textContent
    if (data.last === true) {
      flushPendingAiText()
    }
    return
  }

  if (textContent) {
    addMessage({
      content: textContent,
      type: 'text',
      isUser: false
    })
    log(`收到文本消息: ${textContent}`, 'info')
  }
}

// =============================
// 消息发送
// =============================

export function sendJsonMessage(data: Record<string, any>): boolean {
  if (workbenchConnection) {
    if (!connectionStatus.conversationSessionId || !connectionStatus.connectionId) return false
    data = { ...data,
      conversationSessionId: connectionStatus.conversationSessionId,
      connectionId: connectionStatus.connectionId,
      threadId: connectionStatus.threadId,
      sourceType: connectionStatus.sourceType,
    }
  }
  // 优先使用文本专用连接，否则使用主连接
  const socket = (textSocket && textSocket.readyState === WebSocket.OPEN) ? textSocket : webSocket

  if (!socket || socket.readyState !== WebSocket.OPEN) {
    log('WebSocket未连接，无法发送消息', 'error')
    return false
  }

  try {
    const message = JSON.stringify(data)
    socket.send(message)
    return true
  } catch (error) {
    log(`发送JSON消息失败: ${error}`, 'error')
    return false
  }
}

// 发送二进制数据（用于音频帧）
export function sendBinaryMessage(data: ArrayBuffer | Uint8Array): boolean {
  // 优先使用二进制专用连接，否则使用主连接
  const socket = (binarySocket && binarySocket.readyState === WebSocket.OPEN) ? binarySocket : webSocket

  if (!socket || socket.readyState !== WebSocket.OPEN) {
    log('WebSocket未连接，无法发送二进制数据', 'error')
    return false
  }

  try {
    const frameNo = ++binarySendFrameCounter
    const socketType = socket === binarySocket ? 'binary' : 'main'
    socket.send(data)
    if (shouldLogRealtimeFrame(frameNo)) {
      console.info(
        `[websocket-binary-send] socket=${socketType} frame=${frameNo} ` +
          `len=${data.byteLength} head=${formatBinaryHeadForDebug(data)}`
      )
    }
    return true
  } catch (error) {
    log(`发送二进制数据失败: ${error}`, 'error')
    return false
  }
}

/** 等待工作台可信建联通知，避免仅 TCP open 就开始输入。 */
export function waitForWorkbenchSessionInit(timeoutMs = 5000): Promise<ConnectionStatus> {
  if (connectionStatus.conversationSessionId && connectionStatus.connectionId) return Promise.resolve({ ...connectionStatus })
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      unregisterStatusChangeCallback(onStatus)
      reject(new Error('工作台会话初始化超时'))
    }, timeoutMs)
    const onStatus: StatusChangeHandler = status => {
      if (!status.conversationSessionId || !status.connectionId) return
      clearTimeout(timer)
      unregisterStatusChangeCallback(onStatus)
      resolve(status)
    }
    registerStatusChangeCallback(onStatus)
  })
}

export function sendTextMessage(text: string, metadata: Record<string, unknown> = {}): boolean {
  if (!text || !webSocket || webSocket.readyState !== WebSocket.OPEN) {
    return false
  }

  if (workbenchConnection && (!connectionStatus.conversationSessionId || !connectionStatus.connectionId)) return false

  try {
    const message = {
      type: 'listen',
      state: 'text',
      text: text,
      ...(connectionStatus.sourceType === 'WORKBENCH' ? {
        conversationSessionId: connectionStatus.conversationSessionId,
        connectionId: connectionStatus.connectionId,
        threadId: connectionStatus.threadId,
        sourceType: connectionStatus.sourceType,
      } : {}),
      ...metadata,
    }

    const success = sendJsonMessage(message)

    // 发送成功后添加用户消息到列表
    if (success) {
      addMessage({
        content: text,
        type: 'text',
        isUser: true
      })
    }

    return success
  } catch (error) {
    log(`发送文本消息失败: ${error}`, 'error')
    return false
  }
}

export async function startDirectRecording(): Promise<boolean> {
  if (!webSocket || webSocket.readyState !== WebSocket.OPEN) {
    throw new Error('WebSocket未连接')
  }

  try {
    // 发送开始录音命令
    const startMessage = {
      type: 'stt',
      state: 'start'
    }
    sendJsonMessage(startMessage)
    log('已发送开始录音命令', 'info')

    // 开始麦克风录音并发送音频数据
    const { startMicrophoneRecording } = await import('./audio')
    await startMicrophoneRecording()
    log('麦克风录音已启动', 'success')

    return true
  } catch (error) {
    log(`开始录音失败: ${error}`, 'error')
    throw error
  }
}

export async function stopDirectRecording(): Promise<boolean> {
  try {
    // 停止麦克风录音
    const { stopMicrophoneRecording } = await import('./audio')
    stopMicrophoneRecording()
    log('麦克风录音已停止', 'info')

    // 发送停止录音命令
    if (webSocket && webSocket.readyState === WebSocket.OPEN) {
      const stopMessage = {
        type: 'stt',
        state: 'stop'
      }
      sendJsonMessage(stopMessage)
      log('已发送停止录音命令', 'info')
    }

    return true
  } catch (error) {
    log(`停止录音失败: ${error}`, 'error')
    throw error
  }
}

// =============================
// 连接控制
// =============================

export async function reconnectToServer(config: WebSocketConfig): Promise<boolean> {
  try {
    log('手动触发重连...', 'info')

    await disconnectFromServer()

    _reconnectAttempts = 0

    return await connectToServer(config)
  } catch (error) {
    log(`手动重连失败: ${error}`, 'error')
    connectionStatus.connectionStatus = '重连失败'
    notifyStatusChange()
    return false
  }
}

export function stopAutoReconnect(): boolean {
  try {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
      log('已停止自动重连', 'info')
    }

    _reconnectAttempts = 0

    if (connectionStatus.connectionStatus.includes('重连')) {
      connectionStatus.connectionStatus = '已停止重连'
      notifyStatusChange()
    }

    return true
  } catch (error) {
    log(`停止自动重连失败: ${error}`, 'error')
    return false
  }
}

export function disconnectFromServer(reason?: 'USER_CLOSE' | 'LOGOUT'): boolean {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer)
    reconnectTimer = null
  }

  // 关闭所有连接
  closeAllSockets(reason)
  connectionStatus.sessionId = null
  connectionStatus.conversationSessionId = null
  connectionStatus.connectionId = null
  connectionStatus.threadId = null
  connectionStatus.sourceType = null

  connectionStatus.isConnected = false
  connectionStatus.connectionStatus = '已断开'
  log('WebSocket连接已断开', 'info')
  notifyStatusChange()

  return true
}

export function isWebSocketConnected(): boolean {
  return (webSocket !== null && webSocket.readyState === WebSocket.OPEN) ||
         (textSocket !== null && textSocket.readyState === WebSocket.OPEN) ||
         (binarySocket !== null && binarySocket.readyState === WebSocket.OPEN)
}

export function getConnectionStatus(): ConnectionStatus {
  return { ...connectionStatus }
}
